import React from 'react';
import {
    ConvectionSource,
    PolygonMeteoForecast,
    PredictedCapacityReply,
    RadRestriction,
    RadRestrictions,
} from 'app/model/SOL49';
import { Menu, notification, Spin } from 'antd';
import Utils from 'app/common/util/Utils';
import { Flex } from 'app/components/common/Flex';
import AppContext from 'app/common/context/AppContext';
import {
    AddToCustomLayerEvent,
    NewCustomLayerEvent,
    OpenTvMonitoredEvents,
    RemoveFromCustomLayerEvent,
    ShowModalEvent,
    UpdateTvSelection,
} from 'app/common/eventbus/Events';
import { MenuUtils } from 'app/components/dashboard/common/MenuUtils';
import { AirspaceRef, FlightLevelBand, TimePeriod } from 'app/model/CORE';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import TimeUtils from 'app/common/util/TimeUtils';
import { ModalHandler } from 'app/components/panels/ModalHandler';
import memoizeOne from 'memoize-one';
import { EntryLoadBar } from 'app/components/dashboard/network/flows/svg/EntryLoadBar';
import { OccupancyLoadBar } from 'app/components/dashboard/network/flows/svg/OccupancyLoadBar';
import { ObjectTypeContextMenu } from 'app/components/dashboard/network/flows/menu/MapContextMenu';
import { ItemType } from 'antd/es/menu/hooks/useItems';
import { TVLoadProps } from 'app/components/dashboard/network/flows/TVLoad';
import { MeasureLoadBar } from 'app/components/dashboard/network/flows/svg/MeasureLoadBar';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import { MonitoredEventType, MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import { HotspotPanel } from 'app/components/panels/measure/HotspotPanel';
import { CustomLayerUtils, MapLayer, MapLayerUtils, MapObjectType } from 'app/components/dashboard/network/map/Map';
import { MapSelectionValues, NewLayerPopup } from 'app/components/dashboard/network/map/MapSelectionPopup';
import AuthorisationUtils from 'app/common/context/AuthorisationUtils';
import ConditionalRender from 'app/common/components/ConditionalRender';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { CloseOutlined, ExclamationOutlined, FallOutlined } from '@ant-design/icons';
import MeasureUtils from 'app/common/util/MeasureUtils';
import TvDetailsPanel from 'app/components/panels/tv/TvDetailsPanel';
import FlightUtils from 'app/common/util/FlightUtils';
import {
    ApplicableScenarioMenuUtils,
    ScenarioMenuState,
} from 'app/components/dashboard/common/ApplicableScenarioMenuUtils';

interface MapPolygonConvectionContextMenuProps extends ObjectTypeContextMenu {
    convections: Array<PolygonMeteoForecast>;
    tvLoads: Array<TVLoadProps>;

    onClose(): void;
}

interface MapPolygonConvectionContextMenuState extends ScenarioMenuState {
    loading: boolean;
    predictedCapacityReply: PredictedCapacityReply;
}

export class MapPolygonConvectionContextMenu extends React.PureComponent<
    MapPolygonConvectionContextMenuProps,
    MapPolygonConvectionContextMenuState
> {
    constructor(props: MapPolygonConvectionContextMenuProps) {
        super(props);
        this.state = {
            loading: false,
            predictedCapacityReply: undefined,
            isApplicableScenarioLoading: false,
        };
    }

    componentDidMount() {
        if (
            (AppContext.getConvectionSource() === ConvectionSource.DWD ||
                AppContext.getConvectionSource() === ConvectionSource.DWD_ICONV) &&
            Utils.isNotEmpty(this.props.tvLoads)
        ) {
            this.setState({ loading: true });
            const obj = {};
            this.props.tvLoads.forEach(tvLoad => (obj[tvLoad.tvName] = tvLoad.tvMeteoAlerts));
            ServiceFactory.sol49Service()
                .getPredictedCapacity(AppContext.getConvectionSource(), obj)
                .then(reply => {
                    if (reply.error) {
                        console.error('Error fetching predicted capacity: ', reply.error);
                        notification.error({
                            message: 'Error fetching predicted capacity',
                            description: reply.error?.message,
                        });
                    } else {
                        this.setState({ predictedCapacityReply: reply.data });
                    }
                })
                .catch(reason => {
                    notification.error({ message: 'Error', description: reason.message, duration: 0 });
                })
                .finally(() => this.setState({ loading: false }));
        }
    }

    render() {
        const convections: PolygonMeteoForecast[] = this.uniqueConvections(this.props.convections);
        if (this.props.group) {
            return (
                <Menu
                    items={[
                        {
                            key: 'polygon-convection-detail',
                            label: <span className="detailed-subpanel-title">Convection ({convections?.length})</span>,
                            children: convections?.flatMap(c => {
                                return [
                                    {
                                        key: `polygon-convection-detail-${c.id}`,
                                        label: this.renderGenericInfo(c),
                                        className: 'menuItemConvection',
                                    },
                                    ...this.getMenuItems(c),
                                ];
                            }),
                        },
                    ]}
                    triggerSubMenuAction="click"
                />
            );
        } else {
            return this.renderConvections(convections);
        }
    }

    private renderConvections(convections: Array<PolygonMeteoForecast>) {
        return convections.map(c => this.renderConvection(c))?.flat();
    }

    private uniqueConvections = memoizeOne((convections: Array<PolygonMeteoForecast>) => {
        //ignore duplicated and merges the tvs that belows to the same convection (on the map they are independent elements)
        const uniqueConvectionsMap = new Map();
        convections.forEach(convection => {
            if (!uniqueConvectionsMap.has(convection.id)) {
                uniqueConvectionsMap.set(convection.id, convection);
            } else {
                const currentValue = uniqueConvectionsMap.get(convection.id);
                const newValue: PolygonMeteoForecast = Object.assign(currentValue, {
                    impactedTvs: Object.assign(currentValue.impactedTvs, convection.impactedTvs),
                });
                uniqueConvectionsMap.set(currentValue.id, newValue);
            }
        });
        return Array.from(uniqueConvectionsMap.values());
    });

    renderConvection(convection: PolygonMeteoForecast) {
        const menuItems = this.getMenuItems(convection);
        return (
            <Flex direction="vertical" key={`${convection.id}`}>
                {this.renderGenericInfo(convection)}
                {this.renderRadRestrictions(convection.radRestrictions)}
                <Flex direction="vertical" className="overflow" style={{ maxHeight: '25em' }}>
                    <Menu
                        key="polygon-convection-detail"
                        className={'polygon-convection-detail'}
                        items={menuItems}
                        triggerSubMenuAction="click"
                    />
                </Flex>
            </Flex>
        );
    }

    private renderGenericInfo(convection: PolygonMeteoForecast) {
        return (
            <Flex direction="vertical" key={`${convection.id}`} style={{ padding: '0 1em' }}>
                <span>Risk Level</span>
                <ConditionalRender condition={AppContext.getConvectionSource() !== ConvectionSource.DWD}>
                    <p
                        style={{ textAlign: 'center', lineHeight: '1.5em' }}
                        className={this.getRiskLevelClassName(convection.level)}>
                        {FlightUtils.getConvectionImpact(convection.level)}
                    </p>
                </ConditionalRender>
                <ConditionalRender condition={AppContext.getConvectionSource() === ConvectionSource.DWD}>
                    <p
                        style={{ textAlign: 'center', lineHeight: '1.5em' }}
                        className={`${convection.convectionFraction?.toLowerCase()}_${convection.likeliness?.toLowerCase()}`}>
                        {`${convection.convectionFraction} ${convection.likeliness}`}
                    </p>
                </ConditionalRender>
                <p style={{ color: 'white', lineHeight: '1em' }}>
                    <span style={{ float: 'left' }}>{this.getFlightLevelString(convection.flightLevelBand)}</span>
                    <span style={{ float: 'right' }}>{this.getTimePeriodString(convection.validityPeriod)}</span>
                </p>
            </Flex>
        );
    }

    private renderRadRestrictions(radRestrictions: RadRestrictions) {
        const toRadChild: (rad: RadRestriction, type: string) => ItemType = (
            radRestriction: RadRestriction,
            type: string,
        ) => {
            const child: ItemType = {
                key: `polygon-rad-detail-${type}-${radRestriction.id}`,
                label: radRestriction.id,
                className: 'menuItemConvection',
                onClick: () => {
                    this.props.onClose();
                    ModalHandler.showApplicableRadRestrictions(radRestriction.id, radRestrictions);
                },
            };
            return child;
        };

        const radChildren: ItemType[] = [];
        const radRestrictionsOffLoad = radRestrictions.offLoadingRadRestrictions.filter(
            value => value.preAgreed === true,
        );
        if (radRestrictionsOffLoad.length > 0) {
            radChildren.push(
                {
                    key: `polygon-rad-detail-offload`,
                    label: 'Offloading RAD',
                    disabled: true,
                    style: { background: 'rgb(58, 58, 58)', fontWeight: 'bold' },
                },
                ...radRestrictionsOffLoad.map(rad => toRadChild(rad, 'offload')),
            );
        }
        const radRestrictionsOnLoad = radRestrictions.onLoadingRadRestrictions.filter(
            value => value.preAgreed === true,
        );
        if (radRestrictionsOnLoad.length > 0) {
            radChildren.push(
                {
                    key: `polygon-rad-detail-onload`,
                    label: 'Onloading RAD',
                    disabled: true,
                    style: { background: 'rgb(58, 58, 58)', fontWeight: 'bold' },
                },
                ...radRestrictionsOnLoad.map(rad => toRadChild(rad, 'onload')),
            );
        }

        const radRestrictionsOffOnLoad = radRestrictions.offLoadingAndOnloadingRadRestrictions.filter(
            value => value.preAgreed === true,
        );
        if (radRestrictionsOffOnLoad.length > 0) {
            radChildren.push(
                {
                    key: `polygon-rad-detail-onload-offload`,
                    label: 'Onloading Offloading RAD',
                    disabled: true,
                    style: { background: 'rgb(58, 58, 58)', fontWeight: 'bold' },
                },
                ...radRestrictionsOffOnLoad.map(rad => toRadChild(rad, 'onload-offload')),
            );
        }
        if (radChildren.length > 0) {
            return (
                <Menu
                    items={[
                        {
                            key: 'polygon-rad-detail',
                            label: (
                                <span className="detailed-subpanel-title">
                                    RAD restrictions (
                                    {radRestrictionsOffLoad.length +
                                        radRestrictionsOnLoad.length +
                                        radRestrictionsOffOnLoad.length}
                                    )
                                </span>
                            ),
                            children: radChildren,
                        },
                    ]}
                    triggerSubMenuAction="hover"
                />
            );
        } else {
            return (
                <Menu
                    items={[
                        {
                            key: 'polygon-rad-detail',
                            label: <span className="detailed-subpanel-title">RAD restrictions (none)</span>,
                            disabled: true,
                        },
                    ]}
                />
            );
        }
    }

    private getRiskLevelClassName(riskLevel: number): string {
        switch (riskLevel) {
            case 0:
                return 'None';
            case 1:
                return 'yellow';
            case 2:
                return 'orange';
            case 3:
                return 'red';
            case 4:
                return AppContext.getConvectionSource() == ConvectionSource.DWD ? 'red' : 'purple';
            case 5:
                return 'purple';
            default:
                return '';
        }
    }

    private getFlightLevelString(flBand: FlightLevelBand): string {
        if (flBand) {
            return `${FlightLevelUtils.toString(flBand.min)} > ${FlightLevelUtils.toString(flBand.max)}`;
        }
    }

    private getTimePeriodString(timePeriod: TimePeriod): string {
        if (timePeriod) {
            return `${TimeUtils.formatHHMM(timePeriod.from)}-${TimeUtils.formatHHMM(timePeriod.to)}`;
        }
    }

    private getMenuItems(convection: PolygonMeteoForecast) {
        const sortedTvs = Object.keys(convection.impactedTvs)
            .sort()
            .reduce((obj, key) => {
                obj[key] = convection.impactedTvs[key];
                return obj;
            }, {});

        const menuItems = this.getSubMenuItems(convection.id, convection.validityPeriod, sortedTvs);
        const tvIds = Object.keys(sortedTvs);
        if (Utils.size(tvIds) > 1 && !AuthorisationUtils.isFmpMapUser()) {
            const allTvsMenuItem = {
                key: `all-tvs-${convection.id}`,
                label: (
                    <Flex direction="vertical">
                        <div>
                            ALL: <span style={{ color: 'white' }}>{tvIds.length} TFV</span>
                        </div>
                    </Flex>
                ),
                children: [
                    {
                        label: 'Add to selection',
                        key: `add-to-selection-all-${convection.id}`,
                        onClick: () => AppContext.eventBus.publish(new UpdateTvSelection('add', tvIds)),
                    },
                    {
                        key: `divider-all-${convection.id}`,
                        type: 'divider',
                    },
                    MenuUtils.addToMapWorkspaceLayerMenu(tvIds, 'polygon-convection'),
                    MenuUtils.addToCustomLayer(tvIds),
                    {
                        label: 'Create a custom layer',
                        key: 'create-custom-layer',
                        onClick: () => this.showCustomLayerNamePopup(tvIds),
                    },
                ],
            };
            menuItems.unshift(allTvsMenuItem);
        }
        return menuItems;
    }

    getSubMenuItems = (
        convectionId: string,
        timePeriod: TimePeriod,
        tvAirspace: {
            [tv: string]: AirspaceRef;
        },
    ): ItemType[] => {
        return Object.keys(tvAirspace).map(tv => {
            const tvLoad = this.getTvLoad(tv, this.props.tvLoads);
            const openMeasureItems = tvLoad.monitoredMeasures
                .filter(mm => mm.measureLoad.some(load => TimePeriodUtils.intersects(load.timePeriod, timePeriod)))
                .map((mm, idx) => ({
                    label: `Open/edit ${mm.measure.measureId.id}`,
                    key: `open-edit-${mm.measure.measureId.id}-${idx}`,
                    onClick: () => ModalHandler.showRegulationEditor(mm.measure.measureId.id, 'reg'),
                }));
            const notes = tvLoad.events.filter(MonitoredEventUtils.isNoteEvent);
            const events = tvLoad.events.filter(MonitoredEventUtils.isWeatherEvent);
            let subItems = undefined;
            if (!AuthorisationUtils.isFmpMapUser()) {
                subItems = [
                    {
                        label: 'Show details (graph)',
                        key: `show-tv-details-${convectionId}-${tv}`,
                        onClick: () => this.handleTvLoadClick(tvLoad),
                    },
                    { key: 'divider-1', type: 'divider' },
                    ...openMeasureItems,
                    {
                        label: 'Create a new regulation',
                        key: 'create-new-regulation-' + tv,
                        onClick: () => {
                            this.props.onClose();
                            ModalHandler.showRegulationEditor(tv, 'tv');
                        },
                    },
                    {
                        label: 'Create a new hotspot',
                        key: 'create-new-hotspot-' + tv,
                        onClick: () => {
                            this.props.onClose();
                            ShowModalEvent.fireNotClosable(
                                <HotspotPanel tv={tv} refLoc={tvLoad.refLoc} />,
                                HotspotPanel.HOTSPOT_PANEL_WIDTH_EM,
                            );
                        },
                    },
                    ApplicableScenarioMenuUtils.applicableScenarioMenu(
                        [tv],
                        null,
                        this.state,
                        (state: MapPolygonConvectionContextMenuState) => this.setState(state),
                    ),
                    { key: 'divider-2', type: 'divider' },
                    {
                        label: 'Add to selection',
                        key: `add-to-selection-${convectionId}-${tv}`,
                        onClick: () => AppContext.eventBus.publish(new UpdateTvSelection('add', [tv])),
                    },
                    {
                        label: 'Impacted Flights',
                        key: `impacted-flights_${convectionId}_${tv}`,
                        onClick: () => {
                            this.props.onClose();
                            const convection = this.props.convections.find(c => c.id === convectionId);
                            ModalHandler.showPolygonConvectionImpactedFlightsForTv(convection, tv);
                        },
                        className: 'menuItemConvection',
                    },
                    { key: 'divider-3', type: 'divider' },
                    { key: 'divider-4', type: 'divider' },
                    {
                        key: 'create-custom-layer-' + tv,
                        label: 'Create a custom layer',
                        onClick: () => this.showCustomLayerNamePopup([tv]),
                    },
                    this.addToCustomLayer(tv),
                    ...this.removeFromCustomLayerMenu(tv),
                    { key: 'divider-5', type: 'divider' },
                    {
                        key: 'add-note-' + tv,
                        label: `${Utils.isNotEmpty(notes) ? 'Show notes' : 'Add a note'}`,
                        onClick: () => {
                            this.props.onClose();
                            OpenTvMonitoredEvents.fire(
                                MonitoredEventType.NOTE,
                                tvLoad.tvName,
                                tvLoad.refLoc,
                                [tvLoad.vsName],
                                tvLoad.monitoredMeasures.map(m => m.measure.measureId.id),
                            );
                        },
                    },
                    {
                        key: 'add-event-' + tv,
                        label: `${Utils.isNotEmpty(events) ? 'Show events' : 'Add an event'}`,
                        onClick: () => {
                            this.props.onClose();
                            OpenTvMonitoredEvents.fire(
                                MonitoredEventType.WEATHER_EVENT,
                                tvLoad.tvName,
                                tvLoad.refLoc,
                                [tvLoad.vsName],
                                tvLoad.monitoredMeasures.map(m => m.measure.measureId.id),
                            );
                        },
                    },
                ];
            }
            return {
                key: `tv-${convectionId}-${tv}`,
                label: this.getLabel(tvLoad, timePeriod),
                children: subItems,
            };
        });
    };

    getTvLoad = memoizeOne((tvName: string, tvLoads: Array<TVLoadProps>): TVLoadProps => {
        return tvLoads.find(tvl => tvl.tvName === tvName);
    });

    private getLabel = (tvLoad: TVLoadProps, timePeriod: TimePeriod) => {
        const predictedCapacity = this.state.predictedCapacityReply?.predictions?.find(pc => pc.tv === tvLoad.tvName);
        const errors = this.state.predictedCapacityReply?.errors?.find(e => e.message.includes(tvLoad.tvName));
        return (
            <Flex grow={false} style={{ gap: '1em' }} onClick={() => this.handleTvLoadClick(tvLoad)}>
                <Flex size="6.5em">
                    <span>TV: </span>
                    <span style={{ color: 'white' }}>{tvLoad.tvName}</span>
                </Flex>
                <Flex size={'1em'}>
                    <Spin size="small" spinning={this.state.loading}>
                        <ConditionalRender condition={predictedCapacity?.impacted}>
                            <FallOutlined style={{ color: 'violet', fontSize: '1.5em' }} />
                        </ConditionalRender>
                        <ConditionalRender condition={errors?.details === 'No inference'}>
                            <CloseOutlined style={{ color: 'red', fontSize: '1.5em' }} />
                        </ConditionalRender>
                        <ConditionalRender
                            condition={!predictedCapacity?.impacted && errors?.details === 'missing hours'}>
                            <ExclamationOutlined style={{ color: 'orange', fontSize: '1.5em' }} />
                        </ConditionalRender>
                    </Spin>
                </Flex>
                <Flex direction="vertical" style={{ alignSelf: 'center' }}>
                    {tvLoad?.entryLoad && (
                        <EntryLoadBar
                            key="entry-load"
                            loadValues={tvLoad?.entryLoad}
                            monitoredPeriod={timePeriod}
                            activePeriods={tvLoad.activePeriods}
                            currentTime={AppContext.getCurrentTime()}
                            drawBottomLine={true}
                            barHeightEm={0.5}
                        />
                    )}
                    {tvLoad?.occupancyLoad &&
                        tvLoad?.occupancyLoad.map(occupancyValues => {
                            return (
                                <OccupancyLoadBar
                                    key={'occupancy-load-' + occupancyValues.durationInMin}
                                    loadValues={occupancyValues.occupancyLoadValues}
                                    monitoredPeriod={timePeriod}
                                    activePeriods={tvLoad.activePeriods}
                                    currentTime={AppContext.getCurrentTime()}
                                    drawBottomLine={true}
                                    barHeightEm={0.5}
                                />
                            );
                        })}
                    {tvLoad?.monitoredMeasures?.map(measure => (
                        <MeasureLoadBar
                            key={'measure-load-' + measure.measure.measureId.id}
                            loadValues={measure.measureLoad}
                            monitoredPeriod={timePeriod}
                            currentTime={AppContext.getCurrentTime()}
                            barHeightEm={0.5}
                        />
                    ))}
                </Flex>
            </Flex>
        );
    };

    removeFromCustomLayerMenu(tvName: string) {
        const tvCustomLayers = this.getTvCustomLayers(tvName);
        if (tvCustomLayers?.length > 0) {
            return [
                { key: 'divider-' + tvName, type: 'divider' },
                {
                    key: 'remove-from-layer-' + tvName,
                    label: 'Remove from custom layer',
                    children: [
                        ...tvCustomLayers.map(customLayer => ({
                            key: 'remove-from-layer-' + customLayer.id + tvName,
                            label: `Remove from: ${customLayer.label}`,
                            onClick: () => this.removeFromCustomLayerMenuHandleUpdateSection(tvName, customLayer),
                        })),
                    ],
                },
            ];
        }
        return [];
    }

    addToCustomLayer(tvName: string) {
        const result = {
            label: 'Add to custom layer',
            key: 'add-to-custom-layer-menu-' + tvName,
            children: [
                ...CustomLayerUtils.customLayers
                    .filter(customLayer => !CustomLayerUtils.containsTv(customLayer, tvName))
                    .map(customLayer => ({
                        key: 'add-to-layer-' + customLayer.id + tvName,
                        label: `Add to: ${customLayer.label}`,
                        onClick: () => this.addToCustomLayerMenuHandleUpdateSection(tvName, customLayer),
                    })),
            ],
        };

        if (result.children.length > 0) {
            return result;
        }
    }

    getTvCustomLayers(tvName: string) {
        return CustomLayerUtils.customLayers.filter(
            customLayer =>
                customLayer.objectsToDisplay.filter(
                    objectToDisplay =>
                        MapObjectType.TV === objectToDisplay.type &&
                        tvName === MapLayerUtils.EXTENDED_MONITORED_TV_TV_ID_PROVIDER.getKey(objectToDisplay.object),
                )?.length > 0,
        );
    }

    addToCustomLayerMenuHandleUpdateSection(tvName: string, customLayer: MapLayer) {
        this.props.onClose();
        AddToCustomLayerEvent.fire(customLayer, tvName, MapObjectType.TV);
    }

    removeFromCustomLayerMenuHandleUpdateSection(tvName: string, customLayer: MapLayer) {
        this.props.onClose();
        RemoveFromCustomLayerEvent.fire(customLayer, tvName, MapObjectType.TV);
    }

    showCustomLayerNamePopup(tvNames: string[]) {
        const value: MapSelectionValues = Object.assign(
            {},
            {
                [MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD]: {
                    label: 'TFV',
                    options: tvNames,
                    selected: tvNames,
                },
            },
        );
        const content = <NewLayerPopup onSave={name => this.handleSaveNewLayer(value, name)} />;
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, '200px'));
    }

    handleSaveNewLayer = (value: MapSelectionValues, name: string) => {
        const values: Map<MapObjectType, Array<string>> = new Map<MapObjectType, Array<string>>();
        values.set(MapObjectType.TV, value[MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD].selected);
        NewCustomLayerEvent.fire(name, values);
    };

    handleTvLoadClick(tvLoad: TVLoadProps) {
        this.props.onClose();
        const monitoredMeasuresForTv = tvLoad.monitoredMeasures;
        const regulationPeriods = Utils.isNotEmpty(monitoredMeasuresForTv)
            ? monitoredMeasuresForTv.map(monitoredMeasure =>
                  MeasureUtils.getRegulationPeriods(monitoredMeasure.measure),
              )
            : null;
        const predictedCapacity =
            this.state.predictedCapacityReply?.predictions?.find(pc => pc.tv === tvLoad.tvName)?.predictedCapacity ||
            [];
        const modalContent = (
            <TvDetailsPanel
                currentTime={AppContext.getCurrentTime()}
                tvId={tvLoad.tvName}
                tvSet={tvLoad.vsName}
                regulationPeriods={regulationPeriods}
                occupancyPeriods={undefined}
                predictedCapacity={predictedCapacity}
            />
        );
        AppContext.eventBus.publish(
            new ShowModalEvent(ShowModalEvent.NAME, modalContent, TvDetailsPanel.INITIAL_WIDTH),
        );
    }
}
